<script setup lang="ts">
import { ref } from 'vue'
import { ArrowLeft, Mail, Lock, Eye, EyeOff } from 'lucide-vue-next'
import { useAuthFirebase } from '../composables/useAuthFirebase'
import { useMfaLogin } from '../composables/useMfaLogin'
import { getMfaErrorMessage } from '../constants/errorMessages'
import AuthDialog from '../components/AuthDialog.vue'
import MfaVerificationDialog from '../components/MfaVerificationDialog.vue'

definePageMeta({
  layout: 'auth'
})

const { isAuthenticated, initAuth } = useAuthFirebase()

interface LoginForm {
  email: string
  password: string
  rememberMe: boolean
}

interface FormErrors {
  email?: string
  password?: string
}

const form = ref<LoginForm>({
  email: '',
  password: '',
  rememberMe: false
})

const errors = ref<FormErrors>({})
const { loginWithFirebase, completeMfaLogin, isLoading: authLoading, error } = useAuthFirebase()
const { sendLoginCode, verifyLoginCode, isLoading: mfaLoading } = useMfaLogin()
const showPassword = ref(false)
const showErrorDialog = ref(false)
const errorMessage = ref('')

// 2FA state
const showMfaDialog = ref(false)
const mfaUser = ref<any>(null)
const mfaFirebaseUid = ref('')
const mfaRemainingTime = ref(0)
const mfaTimer = ref<NodeJS.Timeout | null>(null)
const isVerifyingMfa = ref(false)
const mfaDialogRef = ref<any>(null)

// Combined loading state - true when either auth or MFA is loading
const isLoading = computed(() => authLoading.value || mfaLoading.value || showMfaDialog.value)

const validateForm = (): boolean => {
  errors.value = {}
  
  if (!form.value.email) {
    errors.value.email = 'Email is required'
  } else if (!/\S+@\S+\.\S+/.test(form.value.email)) {
    errors.value.email = 'Email is invalid'
  }
  
  if (!form.value.password) {
    errors.value.password = 'Password is required'
  } else if (form.value.password.length < 8) {
    errors.value.password = 'Password must be at least 8 characters'
  }
  
  return Object.keys(errors.value).length === 0
}

const handleSubmit = async () => {
  if (!validateForm()) return
  
  try {
    // Firebase Authentication
    const result = await loginWithFirebase(form.value.email, form.value.password, redirectTo.value)
    
    // Check if 2FA is required
    if (result && result.requiresMfa) {
      mfaUser.value = result.user
      mfaFirebaseUid.value = result.firebaseUid
      
      showMfaDialog.value = true
      
      // Send 2FA code in background
      sendMfaCode()
    }
    
    // Commented out Laravel authentication
    // await login(form.value.email, form.value.password, form.value.rememberMe, redirectTo.value)
  } catch (err: any) {
    // Handle login errors with dialog
    if (err.message) {
      errorMessage.value = err.message
      showErrorDialog.value = true
    } else {
      errorMessage.value = 'Login failed. Please check your credentials and try again.'
      showErrorDialog.value = true
    }
  }
}

// 2FA Methods
const sendMfaCode = async () => {
  try {
    const response = await sendLoginCode(mfaFirebaseUid.value)
    mfaRemainingTime.value = response.expires_in || 300
    startMfaTimer()
  } catch (err: any) {
    errorMessage.value = err.message || 'Failed to send verification code'
    showErrorDialog.value = true
  }
}

const handleMfaVerify = async (code: string) => {
  try {
    // Clear any previous errors
    mfaDialogRef.value?.clearError()
    isVerifyingMfa.value = true
    
    await completeMfaLogin(mfaFirebaseUid.value, code)
    showMfaDialog.value = false
    clearMfaTimer()
  } catch (err: any) {
    const errorMessage = getMfaErrorMessage(err)
    mfaDialogRef.value?.setError(errorMessage)
  } finally {
    isVerifyingMfa.value = false
  }
}

const handleMfaResend = async () => {
  try {
    // Clear any previous errors
    mfaDialogRef.value?.clearError()
    await sendMfaCode()
  } catch (err: any) {
    // Set error message in the MFA dialog
    let errorMessage = 'Failed to resend code. Please try again.'
    
    if (err.response?.status === 429) {
      errorMessage = 'Too many requests. Please wait a few minutes before requesting another code.'
    } else if (err.message) {
      errorMessage = err.message
    }
    
    mfaDialogRef.value?.setError(errorMessage)
  }
}

const startMfaTimer = () => {
  clearMfaTimer()
  mfaTimer.value = setInterval(() => {
    mfaRemainingTime.value--
    if (mfaRemainingTime.value <= 0) {
      clearMfaTimer()
    }
  }, 1000)
}

const clearMfaTimer = () => {
  if (mfaTimer.value) {
    clearInterval(mfaTimer.value)
    mfaTimer.value = null
  }
  mfaRemainingTime.value = 0
}

// Get redirect URL from query params
const route = useRoute()
const redirectTo = computed(() => route.query.redirectTo as string || '/dashboard')

onBeforeMount(() => {
  // Initialize Firebase auth state listener
  initAuth()
  
  // Check for pending 2FA on page load/refresh
  if (import.meta.client) {
    const tempSession = useSessionStorage<{
      firebaseUid: string,
      redirectTo: string,
      timestamp: number,
      userId: string,
      email: string
    }>('auth.temp', null);
    
    if (tempSession.value && tempSession.value.firebaseUid) {
      const { isFirebaseAuthenticated, firebaseUser } = useAuthFirebase()
      
      if (isFirebaseAuthenticated.value && firebaseUser.value) {
        mfaUser.value = {
          email: tempSession.value.email || firebaseUser.value.email,
          id: tempSession.value.userId
        }
        mfaFirebaseUid.value = tempSession.value.firebaseUid
        showMfaDialog.value = true
        
        const timeElapsed = Date.now() - (tempSession.value.timestamp || 0)
        const fiveMinutes = 5 * 60 * 1000
        
        if (timeElapsed < fiveMinutes) {
          mfaRemainingTime.value = Math.max(0, Math.floor((fiveMinutes - timeElapsed) / 1000))
          if (mfaRemainingTime.value > 0) {
            startMfaTimer()
          }
        } else {
          sendMfaCode()
        }
      } else {
        tempSession.value = null
      }
    }
  }
  
  // The middleware will handle redirecting authenticated users
})
</script>

<template>
  <div class="min-h-screen flex bg-brand-bg">
    <!-- Left: Login Form -->
    <div class="w-full md:w-1/2 flex flex-col justify-center px-8 py-12 bg-white">
      <div class="max-w-md w-full mx-auto space-y-8">
        <!-- Back to home button
        <div class="text-left mb-6">
          <button
            class="flex items-center text-brand-blue hover:text-brand-green text-sm cursor-pointer w-fit"
            @click="navigateTo('/')"
          >
            <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" /></svg>
            <span>Back to Home</span>
          </button>
        </div>
        -->
        <!-- Welcome Message -->
        <h1 class="text-4xl font-extrabold text-brand-blue mb-2">Holla,<br />Welcome Back</h1>
        <p class="text-brand-green mb-8">Hey, welcome back let's get you signed in</p>
        <form class="space-y-6" @submit.prevent="handleSubmit">
          <!-- Email Input -->
          <div class="space-y-2">
            <input
              id="email"
              v-model="form.email"
              type="email"
              class="block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
              placeholder="stanley@gmail.com"
              required
            />
            <p v-if="errors.email" class="text-sm text-red-600">{{ errors.email }}</p>
          </div>
          <!-- Password Input -->
          <div class="space-y-2">
            <div class="relative">
              <input
                id="password"
                v-model="form.password"
                :type="showPassword ? 'text' : 'password'"
                class="block w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                placeholder="Password"
                required
              />
              <button
                type="button"
                class="absolute inset-y-0 right-0 pr-3 flex items-center"
                @click="showPassword = !showPassword"
              >
                <component
                  :is="showPassword ? EyeOff : Eye"
                  class="h-5 w-5 text-gray-400"
                />
              </button>
            </div>
            <p v-if="errors.password" class="text-sm text-red-600">{{ errors.password }}</p>
          </div>
          <!-- Remember me and forgot password -->
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <input
                id="remember-me"
                name="remember-me"
                type="checkbox"
                class="h-4 w-4 text-brand-green focus:ring-brand-green border-gray-300 rounded"
                v-model="form.rememberMe"
              />
              <label for="remember-me" class="ml-2 block text-sm text-brand-blue">
                Remember me
              </label>
            </div>
            <div class="text-sm">
              <NuxtLink to="/forgot-password" class="font-medium text-brand-green hover:text-brand-blue" @click="navigateTo('/forgot-password')">
                Forgot Password?
              </NuxtLink>
            </div>
          </div>
          <!-- Submit Button -->
          <div>
            <button
              type="submit"
              :disabled="isLoading"
              class="w-full flex justify-center py-2 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-blue hover:bg-brand-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue transition-colors duration-200"
            >
              <span v-if="isLoading" class="flex items-center">
                <div class="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {{ showMfaDialog ? 'Verifying...' : 'Signing in...' }}
              </span>
              <span v-else>Sign In</span>
            </button>
          </div>
        </form>
        <!-- Error display - Commented out in favor of dialog -->
        <!-- <div v-if="error" class="mt-4 p-4 bg-red-50 rounded-lg">
          <p class="text-sm text-red-600">Invalid credentials</p>
        </div> -->
        <!-- Sign Up Link - Registration disabled -->
        <!-- <div class="mt-8 text-center text-sm text-brand-green">
          Don't have an account?
          <NuxtLink to="/register" class="text-brand-blue font-medium hover:underline">Sign Up</NuxtLink>
        </div> -->
      </div>
    </div>
    <!-- Right: Illustration -->
    <div class="hidden md:flex w-1/2 relative overflow-hidden">
      <!-- Gray placeholder background -->
      <div class="absolute inset-0 bg-gray-300 opacity-60 z-0"></div>
      <!-- Color overlay for color scheme unification -->
      <div class="absolute inset-0 bg-brand-blue opacity-20 z-10"></div>
      <img src="/illustration.jpg" alt="Contract Signing Illustration" class="w-full h-full object-cover relative z-20" />
    </div>
  </div>

  <!-- Error Dialog -->
  <AuthDialog
    :is-open="showErrorDialog"
    type="error"
    title="Login Failed"
    :message="errorMessage"
    @close="showErrorDialog = false"
  />

  <!-- 2FA Verification Dialog -->
  <MfaVerificationDialog
    ref="mfaDialogRef"
    :is-open="showMfaDialog"
    :is-loading="isVerifyingMfa"
    :user-email="mfaUser?.email"
    :remaining-time="mfaRemainingTime"
    @close="showMfaDialog = false"
    @verify="handleMfaVerify"
    @verify-backup="handleMfaVerify"
    @resend="handleMfaResend"
  />
</template>
